import { Box, Container, Paper, Typography, useMediaQuery, useTheme } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import LoginIcon from "@mui/icons-material/Login";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import { useKeycloak } from "../../context/KeycloakContext";
import { AppRoutes } from "../../navigation/AppRoutes";
import { SessionAuthKey } from "../../model/Config";
import { ParseToken } from "../../model/Functions";
import { useI18n } from "../../context/I18nContext";
import logoBlack from "../../assets/logo-black.png";
import logoWhite from "../../assets/logo-white.png";

export function LoginPage() {
  const { keycloak, initialized } = useKeycloak();
  const nav = useNavigate();
  const { t } = useI18n();
  const theme = useTheme();
  const s = theme.palette.saas;
  const isLight = s.scheme === "light";
  const isWide = useMediaQuery(theme.breakpoints.up("md"));
  const brandLogo = isLight ? logoBlack : logoWhite;

  useEffect(() => {
    if (initialized && keycloak?.token) {
      localStorage.setItem(SessionAuthKey, keycloak.token);
      ParseToken(keycloak.token);
      nav(AppRoutes.CATALOG, { replace: true });
    }
  }, [keycloak, initialized, nav]);

  const handleLogin = async () => {
    if (!keycloak) return;
    try {
      await keycloak.login({
        redirectUri: window.location.origin + AppRoutes.BASENAME + "/",
      });
    } catch (error) {
      console.error("Erreur lors de la connexion:", error);
    }
  };

  const logoBox = (size: number) => (
    <Box
      sx={{
        p: 3,
        borderRadius: "20px",
        background: isLight
          ? "linear-gradient(135deg, rgba(21, 101, 192, 0.12) 0%, rgba(123, 31, 162, 0.1) 100%)"
          : `linear-gradient(135deg, ${alpha(s.accentBlue, 0.18)} 0%, ${alpha(s.accentPurple, 0.16)} 100%)`,
        border: `1px solid ${s.borderStrong}`,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: isLight ? "0 8px 24px rgba(21, 101, 192, 0.12)" : "0 8px 28px rgba(0, 0, 0, 0.35)",
      }}
    >
      <img
        src={brandLogo}
        alt="CodeFirst"
        style={{
          height: size,
          width: "auto",
          maxWidth: 200,
          display: "block",
          filter: `drop-shadow(0 4px 12px ${alpha(s.accentBlue, 0.35)})`,
        }}
      />
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: isLight
          ? `linear-gradient(135deg, ${s.pageBg} 0%, ${s.surfaceHover} 45%, ${s.pageBg} 100%)`
          : `linear-gradient(135deg, ${s.pageBg} 0%, ${s.dialogBg} 50%, ${s.pageBg} 100%)`,
        display: "flex",
        alignItems: "stretch",
        justifyContent: "center",
        position: "relative",
        overflow: "hidden",
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          background: isLight
            ? "radial-gradient(ellipse at top, rgba(21, 101, 192, 0.08) 0%, transparent 50%), radial-gradient(ellipse at bottom, rgba(123, 31, 162, 0.06) 0%, transparent 50%)"
            : `radial-gradient(ellipse at top, ${alpha(s.accentBlue, 0.1)} 0%, transparent 52%), radial-gradient(ellipse at bottom, ${alpha(s.accentPurple, 0.1)} 0%, transparent 50%)`,
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: "stretch",
          width: "100%",
          maxWidth: { md: 960 },
          minHeight: { md: "100vh" },
          m: "auto",
          position: "relative",
          zIndex: 1,
        }}
      >
        {isWide && (
          <Box
            sx={{
              flex: "0 0 42%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              px: 4,
              py: 6,
              borderRight: `1px solid ${s.borderStrong}`,
              background: isLight
                ? `linear-gradient(160deg, rgba(21, 101, 192, 0.08) 0%, ${s.surface} 50%, rgba(123, 31, 162, 0.06) 100%)`
                : `linear-gradient(160deg, ${alpha(s.accentBlue, 0.12)} 0%, rgba(22, 27, 34, 0.55) 45%, ${alpha(s.accentPurple, 0.1)} 100%)`,
            }}
          >
            <Box sx={{ mb: 3 }}>{logoBox(64)}</Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                textAlign: "center",
                mb: 2,
                background: s.titleGradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              {t("auth.loginTitle")}
            </Typography>
            <Typography variant="body1" sx={{ color: s.textMuted, textAlign: "center", maxWidth: 320 }}>
              {t("auth.loginSubtitle")}
            </Typography>
          </Box>
        )}

        <Container
          maxWidth="sm"
          sx={{ flex: 1, display: "flex", alignItems: "center", py: { xs: 4, md: 6 } }}
        >
          <Paper
            elevation={isLight ? 2 : 0}
            sx={{
              p: { xs: 4, md: 6 },
              borderRadius: "24px",
              bgcolor: s.surface,
              border: `1px solid ${s.border}`,
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              width: "100%",
              boxShadow: isLight ? "0 12px 40px rgba(15, 23, 42, 0.08)" : undefined,
              "&::before": {
                content: '""',
                position: "absolute",
                inset: 0,
                background: isLight
                  ? "linear-gradient(45deg, rgba(21, 101, 192, 0.04) 0%, rgba(123, 31, 162, 0.03) 50%, rgba(46, 125, 50, 0.03) 100%)"
                  : `linear-gradient(45deg, ${alpha(s.accentBlue, 0.05)} 0%, ${alpha(s.accentPurple, 0.04)} 50%, rgba(22, 27, 34, 0.06) 100%)`,
                pointerEvents: "none",
              },
            }}
          >
            {/* Logo (mobile / tablette ; masqué sur grand écran : panneau gauche) */}
            <Box
              sx={{
                mb: 4,
                display: { xs: "flex", md: "none" },
                justifyContent: "center",
                position: "relative",
                zIndex: 1,
              }}
            >
              {logoBox(56)}
            </Box>

            {/* Titre */}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1.5,
                background: s.titleGradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                position: "relative",
                zIndex: 1,
              }}
            >
              {t("auth.loginTitle")}
            </Typography>

            {/* Sous-titre */}
            <Typography
              variant="body1"
              sx={{ color: s.textMuted, mb: 4, position: "relative", zIndex: 1 }}
            >
              {t("auth.loginSubtitle")}
            </Typography>

            {/* Séparateur */}
            <Box
              sx={{
                width: "80px",
                height: "3px",
                background: `linear-gradient(90deg, ${s.accentBlue}, ${s.accentPurple})`,
                borderRadius: "2px",
                margin: "0 auto",
                mb: 4,
                position: "relative",
                zIndex: 1,
              }}
            />

            {/* Bouton de connexion */}
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                component="button"
                onClick={handleLogin}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 2,
                  px: 5,
                  py: 2,
                  background: isLight
                    ? `linear-gradient(135deg, ${s.accentBlue} 0%, ${s.accentPurple} 100%)`
                    : s.titleGradient,
                  borderRadius: "16px",
                  border: `1px solid ${isLight ? s.borderStrong : alpha(s.textPrimary, 0.2)}`,
                  color: s.textOnPrimaryAction,
                  fontSize: "1.1rem",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.3s ease",
                  boxShadow: isLight ? `0 8px 32px ${alpha(s.accentBlue, 0.4)}` : "0 8px 28px rgba(0, 0, 0, 0.45)",
                  "&:hover": {
                    transform: "translateY(-4px) scale(1.02)",
                    boxShadow: isLight ? `0 16px 48px ${alpha(s.accentBlue, 0.5)}` : `0 12px 36px ${alpha(s.accentBlue, 0.22)}`,
                    background: isLight
                      ? `linear-gradient(135deg, ${alpha(s.accentBlue, 0.92)} 0%, ${s.accentPurple} 100%)`
                      : `linear-gradient(135deg, ${alpha(s.accentBlue, 0.95)} 0%, ${alpha(s.accentPurple, 0.95)} 100%)`,
                  },
                  "&:active": { transform: "translateY(-2px) scale(1)" },
                }}
              >
                <LoginIcon sx={{ fontSize: 24 }} />
                {t("auth.loginButton")}
              </Box>
            </Box>

            {/* Footer */}
            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: `1px solid ${s.divider}`,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <RocketLaunchIcon sx={{ fontSize: 16, color: s.textMuted }} />
                <Typography variant="caption" sx={{ color: s.textMuted }}>
                  Propulsé par CodeFirst
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Container>
      </Box>
    </Box>
  );
}
